import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import {
  getTranslation,
  IconFooterLogo,
  Icon,
  Link,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers/resyConfigSlice';
import { IDLE } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import Twitter from '../../../../../ui-kitchen/src/svg/icons/twitter.svg';
import Facebook from '../../svg/dynamic/Facebook.svg';
import Instagram from '../../svg/dynamic/Instagram.svg';
import appendCurrentQueryParams from '../../utils/appendCurrentQueryParams';
import './Footer.scss';

const COLLECTION_TYPE_IDS = {
  CLIMBING: 3,
  NEW: 4,
  TOP_RATED: 5,
};

const CLICK_LINK_EVENT = 'click link';

const Footer = ({
  $rootScope,
  $location,
  Analytics,
  collections,
  dispatch,
  isGdaEnabled,
  configStatus,
  selectedLocation,
}) => {
  const [renderCount, setRenderCount] = useState(0);
  const [search, setSearch] = useState({
    date: moment().format('YYYY-MM-DD'),
    seats: 2,
  });
  const [filteredCollections, setFilteredCollections] = useState({});

  const context = utils.getPageContext(window.location.pathname) ?? 'homepage';

  const getCollections = () => {
    const typeKeys = Object.keys(COLLECTION_TYPE_IDS);
    const typeValues = Object.values(COLLECTION_TYPE_IDS);

    if (collections?.all) {
      const filtered = collections.all.reduce((total, item) => {
        const collectionsById = total;
        const index = typeValues.indexOf(item.info.type_id);
        if (index >= 0) {
          collectionsById[typeKeys[index]] = item;
        }
        return collectionsById;
      }, {});
      setFilteredCollections(filtered);
    }
  };

  const onSelectedLocationChange = () => {
    const searchParams = $location.search();

    if (searchParams.date && searchParams.seats) {
      setSearch(searchParams);
    }

    if (selectedLocation?.code) {
      getCollections();
    }
  };

  const initTranslationListener = () => {
    $rootScope.$on('$translateChangeSuccess', () => {
      setRenderCount((prev) => prev + 1);
    });
  };

  const initGetConfig = async () => {
    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
  };

  const handleLinkClick = (label) => {
    Analytics.event(CLICK_LINK_EVENT, {
      context,
      value: label,
    });
  };

  useEffect(() => {
    initTranslationListener();
    initGetConfig();
  }, []);

  useEffect(() => {
    getCollections();
  }, [collections]);

  useEffect(() => {
    onSelectedLocationChange();
  }, [selectedLocation?.code]);

  const blogUrlSlug = selectedLocation?.url_slug
    ?.split('-')
    .slice(0, -1)
    .join('-');
  const date = new Date().getFullYear();
  const instagramHref = selectedLocation?.code === 'ldn'
    ? 'https://www.instagram.com/resylondon'
    : 'https://www.instagram.com/resy';
  const nearby = `/cities/${selectedLocation?.url_slug}/search`;
  const climbing = filteredCollections.CLIMBING
    ? `/cities/${selectedLocation?.url_slug}/list/${filteredCollections.CLIMBING.info.collection_slug}?date=${search.date}&seats=${search.seats}`
    : null;
  const topRated = filteredCollections.TOP_RATED
    ? `/cities/${selectedLocation?.url_slug}/list/${filteredCollections.TOP_RATED.info.collection_slug}?date=${search.date}&seats=${search.seats}`
    : null;
  const newOnResy = filteredCollections.NEW
    ? `/cities/${selectedLocation?.url_slug}/list/${filteredCollections.NEW.info.collection_slug}?date=${search.date}&seats=${search.seats}`
    : null;
  const events = `/cities/${selectedLocation?.url_slug}/events`;
  const stories = selectedLocation?.blog_url || 'https://blog.resy.com/';
  const resyGiftCardUrl = 'https://www.amexgiftcard.com/resy-gift.html';
  const guides = selectedLocation?.url_slug
    ? `https://blog.resy.com/category/guides/?city=${blogUrlSlug}`
    : 'https://blog.resy.com/category/guides/';
  const hitList = selectedLocation?.url_slug
    ? `https://blog.resy.com/category/the-hit-list/?city=${blogUrlSlug}`
    : 'https://blog.resy.com/category/the-hit-list/';
  const exclusiveUSOffers = 'https://www.americanexpress.com/en-us/credit-cards/offers/resy/?extlink=resy-homepage-bottom-nav&eep=46100';
  const footerLinkVariantName = 'footer';
  const blankLinkTarget = '_blank';

  const hasBottomNavBar = !$rootScope.hideSelectors;
  const resyJoinBaseUrl = appendCurrentQueryParams('https://resy.com/join/');
  const resyJoinUrl = (pathSuffix = '') => {
    const [base, baseQuery = ''] = resyJoinBaseUrl.split('?');
    const baseParams = new URLSearchParams(baseQuery);
    const [pathPartWithQuery, hash = ''] = pathSuffix.split('#');
    const [pathPart, pathQuery = ''] = pathPartWithQuery.split('?');
    const pathParams = new URLSearchParams(pathQuery);

    pathParams.forEach((value, key) => {
      baseParams.set(key, value);
    });

    const normalizedBase = base.endsWith('/') ? base : `${base}/`;
    const normalizedPath = pathPart.replace(/^\//, '');
    const fullUrl = normalizedPath
      ? `${normalizedBase}${normalizedPath}`
      : normalizedBase;
    const mergedSearch = baseParams.toString();
    const urlWithQuery = mergedSearch ? `${fullUrl}?${mergedSearch}` : fullUrl;

    return hash ? `${urlWithQuery}#${hash}` : urlWithQuery;
  };

  return (
    <footer
      data-testid="footer"
      className={`Footer${hasBottomNavBar ? ' Footer__bottomNavBar' : ''}`}
      id="page-footer"
    >
      {$rootScope.previewMode && (
        <div data-testid="footer-disabled" className="Footer--disabled" />
      )}
      <div className="container">
        <div className="Footer__head Footer__col-container">
          <div className="Footer__logo Footer__col-left">
            <IconFooterLogo />
          </div>
          <div className="Footer__head__links Footer__col-right">
            <div className="inner-container">
              <ul>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.about"
                  >
                    <Link variant={footerLinkVariantName} href="about-and-help">
                      About
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.careers"
                  >
                    <Link variant={footerLinkVariantName} href="careers">
                      Careers
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.newsroom"
                  >
                    <Link
                      variant={footerLinkVariantName}
                      href="https://blog.resy.com/newsroom/"
                    >
                      Newsroom
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.ios_app"
                  >
                    <Link
                      variant={footerLinkVariantName}
                      href="https://apps.apple.com/app/apple-store/id866163372"
                    >
                      iOS App
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.android_app"
                  >
                    <Link
                      variant={footerLinkVariantName}
                      href="https://play.google.com/store/apps/details?id=com.resy.android.prod&hl=en_US&pli=1"
                    >
                      Android App
                    </Link>
                  </TranslationProvider>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="Footer__body Footer__col-container">
          <div className="Footer__col-left">
            <div className="Footer__body__mission">
              <TranslationProvider
                key={renderCount}
                translationKey="footer.tag_line"
              >
                <p>
                  Resy powers some of the world’s best restaurants, using
                  technology to imagine the future of hospitality.
                </p>
              </TranslationProvider>
            </div>

            <div
              className="Footer__body__social"
              aria-label={getTranslation({
                translationKey: '',
                fallback: 'Social media',
              })}
            >
              <div className="inner-container">
                <ul>
                  <li className="instagram">
                    <Link
                      data-testid="instagram"
                      variant={footerLinkVariantName}
                      href={instagramHref}
                      target={blankLinkTarget}
                      aria-label={getTranslation({
                        translationKey: 'resy_instagram',
                        fallback: 'Resy Instagram',
                      })}
                    >
                      <Icon name="Instagram" component={Instagram} />
                    </Link>
                  </li>
                  <li className="twitter">
                    <Link
                      variant={footerLinkVariantName}
                      href="https://twitter.com/resy"
                      target={blankLinkTarget}
                      aria-label={getTranslation({
                        translationKey: 'resy_twitter',
                        fallback: 'Resy Twitter',
                      })}
                    >
                      <Icon name="Twitter" component={Twitter} fill="#737373" />
                    </Link>
                  </li>
                  <li className="facebook">
                    <Link
                      variant={footerLinkVariantName}
                      href="https://www.facebook.com/Resy"
                      target={blankLinkTarget}
                      aria-label={getTranslation({
                        translationKey: 'resy_facebook',
                        fallback: 'Resy Facebook',
                      })}
                    >
                      <Icon name="Facebook" component={Facebook} />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="Footer__col-right Footer__body__links">
            <div className="inner-container">
              <ul>
                <li className="col">
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.head_1"
                  >
                    <h2 className="Footer__body__links__head">
                      Discover &amp; Book
                    </h2>
                  </TranslationProvider>
                  <div className="Footer__body__links__container">
                    <ul className="Footer__body__links__col">
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.nearby"
                        >
                          <Link variant={footerLinkVariantName} href={nearby}>
                            Nearby Restaurants
                          </Link>
                        </TranslationProvider>
                      </li>
                      {selectedLocation?.content_tier < 3
                      && filteredCollections ? (
                        <>
                          {climbing && (
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.climbing"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={climbing}
                                >
                                  Climbing
                                </Link>
                              </TranslationProvider>
                            </li>
                          )}
                          {topRated && (
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.top_rated"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={topRated}
                                >
                                  Top Rated
                                </Link>
                              </TranslationProvider>
                            </li>
                          )}
                          {newOnResy && (
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.new_on_resy"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={newOnResy}
                                >
                                  New on Resy
                                </Link>
                              </TranslationProvider>
                            </li>
                          )}
                          <li>
                            <TranslationProvider
                              key={renderCount}
                              translationKey="footer.events"
                            >
                              <Link
                                variant={footerLinkVariantName}
                                href={events}
                              >
                                Events
                              </Link>
                            </TranslationProvider>
                          </li>
                          <li>
                            <TranslationProvider
                              key={renderCount}
                              translationKey="footer.guides"
                            >
                              <Link
                                variant={footerLinkVariantName}
                                href={guides}
                              >
                                Guides
                              </Link>
                            </TranslationProvider>
                          </li>
                          <li>
                            <TranslationProvider
                              key={renderCount}
                              translationKey="footer.hit_list"
                            >
                              <Link
                                variant={footerLinkVariantName}
                                href={hitList}
                              >
                                The Hit List
                              </Link>
                            </TranslationProvider>
                          </li>
                        </>
                        ) : (
                          <>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.stories"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={stories}
                                >
                                  Stories
                                </Link>
                              </TranslationProvider>
                            </li>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.guides"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={guides}
                                >
                                  Guides
                                </Link>
                              </TranslationProvider>
                            </li>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.hit_list"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={hitList}
                                >
                                  The Hit List
                                </Link>
                              </TranslationProvider>
                            </li>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.events"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={events}
                                >
                                  Events
                                </Link>
                              </TranslationProvider>
                            </li>
                          </>
                        )}
                    </ul>
                    <ul className="Footer__body__links__col">
                      {selectedLocation?.content_tier < 3
                        && filteredCollections && (
                          <>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.stories"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={stories}
                                >
                                  Stories
                                </Link>
                              </TranslationProvider>
                            </li>
                            <li>
                              <TranslationProvider
                                key={renderCount}
                                translationKey="footer.amex_offers"
                              >
                                <Link
                                  variant={footerLinkVariantName}
                                  href={exclusiveUSOffers}
                                  onClick={() => handleLinkClick('footer exclusive offers')}
                                >
                                  U.S. Card Offers from Amex
                                </Link>
                              </TranslationProvider>
                            </li>
                          </>
                      )}
                      {isGdaEnabled && (
                        <li>
                          <TranslationProvider
                            key={renderCount}
                            translationKey="rga"
                          >
                            <Link
                              variant={footerLinkVariantName}
                              href="/global-dining-access"
                              onClick={() => handleLinkClick('footer global dining access')}
                            >
                              Global Dining Access
                            </Link>
                          </TranslationProvider>
                        </li>
                      )}
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.resy_gift_card"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href={resyGiftCardUrl}
                          >
                            Resy Gift Card
                          </Link>
                        </TranslationProvider>
                      </li>
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.security_center"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href="/security-center?utm_source=resy&utm_medium=web-footer&utm_campaign=security-center"
                          >
                            Security Center
                          </Link>
                        </TranslationProvider>
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="col">
                  <TranslationProvider
                    key={renderCount}
                    translationKey="footer.head_2"
                  >
                    <h2 className="Footer__body__links__head">
                      For Businesses
                    </h2>
                  </TranslationProvider>
                  <div className="Footer__body__links__container">
                    <ul className="Footer__body__links__col">
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.book_a_consultation"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href={resyJoinUrl('get-started/')}
                            target="_self"
                          >
                            Book a Demo
                          </Link>
                        </TranslationProvider>
                      </li>
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.resy_for_restaurants"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href={resyJoinUrl()}
                            target="_self"
                          >
                            Resy for Businesses
                          </Link>
                        </TranslationProvider>
                      </li>
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.pricing"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href={resyJoinUrl('plans-pricing/')}
                            target="_self"
                          >
                            Pricing
                          </Link>
                        </TranslationProvider>
                      </li>
                    </ul>
                    <ul className="Footer__body__links__col">
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.resy_os_blog"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href="https://blog.resy.com/for-restaurants/"
                          >
                            Resy OS Blog
                          </Link>
                        </TranslationProvider>
                      </li>
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.resy_help"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href="https://helpdesk.resy.com"
                          >
                            Resy Help Desk
                          </Link>
                        </TranslationProvider>
                      </li>
                      <li>
                        <TranslationProvider
                          key={renderCount}
                          translationKey="footer.resy_os_dashboard"
                        >
                          <Link
                            variant={footerLinkVariantName}
                            href="http://os.resy.com/portal"
                          >
                            Resy OS Dashboard
                          </Link>
                        </TranslationProvider>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="Footer__bottom Footer__col-container">
          <div className="Footer__col-left Footer__bottom__copyright">
            &copy;
            {' '}
            {date}
            {' '}
            Resy Network, Inc.
          </div>
          <div className="Footer__col-right Footer__bottom__legal">
            <div className="inner-container">
              <ul>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="privacy_policy"
                  >
                    <Link variant={footerLinkVariantName} href="privacy">
                      Privacy Policy
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="terms_of_service"
                  >
                    <Link variant={footerLinkVariantName} href="terms">
                      Terms of Service
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="cookie_policy"
                  >
                    <Link variant={footerLinkVariantName} href="cookie-policy">
                      Cookie Policy
                    </Link>
                  </TranslationProvider>
                </li>
                <li>
                  <TranslationProvider
                    key={renderCount}
                    translationKey="accessibility_statement"
                  >
                    <Link
                      variant={footerLinkVariantName}
                      href="accessibility-statement"
                    >
                      Accessibility Statement
                    </Link>
                  </TranslationProvider>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const footerPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $on: PropTypes.func.isRequired,
    hideSelectors: PropTypes.bool,
    previewMode: PropTypes.bool,
  }).isRequired,
  $location: PropTypes.shape({
    search: PropTypes.func.isRequired,
    path: PropTypes.func.isRequired,
  }).isRequired,
  collections: PropTypes.shape({
    all: PropTypes.arrayOf(PropTypes.shape({ reduce: PropTypes.func })),
  }),
  dispatch: PropTypes.func.isRequired,
  isGdaEnabled: PropTypes.bool,
  configStatus: PropTypes.string.isRequired,
  selectedLocation: PropTypes.shape({
    code: PropTypes.string,
    blog_url: PropTypes.string,
    content_tier: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf([null]),
    ]),
    url_slug: PropTypes.string,
  }),
};

Footer.propTypes = footerPropTypes;

Footer.defaultProps = {
  collections: {},
  isGdaEnabled: false,
  selectedLocation: {
    code: 'ny',
    blog_url: 'https://blog.resy.com/city/new-york/',
    url_slug: null,
    content_tier: null,
  },
};

const mapStateToProps = ({ resyConfig, resyLocations }) => ({
  configStatus: resyConfig.configStatus,
  isGdaEnabled: resyConfig.config?.enable_global_dining_access,
  selectedLocation: resyLocations.selected,
});

export { footerPropTypes, Footer as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps)(Footer);
